import {
    catchError,
    concatMap,
    debounceTime,
    defaultIfEmpty,
    filter,
    map,
    mergeMap,
    scan,
    startWith,
    switchMap,
    switchMapTo,
    takeUntil,
} from 'rxjs/operators'
import { isActionOf } from 'typesafe-actions'
import { projectActions } from './project-actions'
import { apiRequests } from '../../../http/config'
import { concat, EMPTY, forkJoin, from, of, throwError } from 'rxjs'
import { infoToast, toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import { push } from 'connected-react-router'
import { ROUTES } from '../../../http/routes'
import { combineEpics } from 'redux-observable'
import { RootEpic } from '../../root'
import { diffAndPrepare, diffEntityArraySimple, getId, isNew } from '../../../utils/entity-utils'
import { EMPTY_PROJECT, ServerProject } from '../../../responses/projects'
import { urlWithParams } from '../../../http/utils'
import { objectsSelector, pipeSelectors, projectObjectsSelector, projectSelector } from '../../../utils/selectors'
import { listFiltersToProjectQuery } from '../../pages/project-list-filters'
import { matchPath } from 'react-router'
import * as A from 'fp-ts/es6/Array'
import { ProjectWorkObject } from '../../../responses/objects'
import { RowResponse } from '../../../responses/response'
import { isSuccess, isSuccessArray } from '../../../utils/type-guards'
import { unbox, unboxRecord } from '../../../utils/utils'
import { TypedAjaxResponse } from '../../../http/types'
import { ProjectEditState } from './project-reducer'

const LIMIT = 50

const get: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.get.request)),
    mergeMap(({payload}) => apiRequests.project.get({
        params: {projectId: payload},
        query: {with: 'full_tasks_structured'},
    })),
    mergeMap(({response}) => response.success ?
        of(projectActions.get.success(response.row)) :
        from([
            projectActions.get.failure(toastFailedResponse(response, 'Не удалось получить проект')),
            push(ROUTES.PROJECTS),
        ])
    ),
)

const list: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.list.init)),
    debounceTime(200),
    switchMap(action => action$.pipe(
        takeUntil(action$.pipe(filter(isActionOf(projectActions.list.failure)))),
        startWith(projectActions.list.more()),
        filter(isActionOf(projectActions.list.more)),
        scan(offset => offset + LIMIT, -LIMIT),
        concatMap(offset => apiRequests.project.list({
                query: {
                    ...listFiltersToProjectQuery(action.payload),
                    limit: LIMIT,
                    offset,
                }
            }).pipe(
            map(res => res.response.success ?
                projectActions.list.success({
                    offset,
                    projects: res.response.rows,
                    total: res.response.total
                }) :
                projectActions.list.failure(toastFailedResponse(res.response, 'Не удалось получить проекты'))
            )
            )
        ),
    )),
)

// todo remove duplicate, simplify code
const archive: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.archive.init)),
    debounceTime(200),
    switchMap(action => action$.pipe(
        takeUntil(action$.pipe(filter(isActionOf(projectActions.archive.failure)))),
        startWith(projectActions.archive.more()),
        filter(isActionOf(projectActions.archive.more)),
        scan(offset => offset + LIMIT, -LIMIT),
        concatMap(offset => apiRequests.project.list({
                query: {
                    ...listFiltersToProjectQuery(action.payload, 1),
                    limit: LIMIT,
                    offset,
                }
            }).pipe(
            map(res => res.response.success ?
                projectActions.archive.success({
                    offset,
                    projects: res.response.rows,
                    total: res.response.total
                }) :
                projectActions.archive.failure(toastFailedResponse(res.response, 'Не удалось получить проекты'))
            )
            )
        ),
    )),
)

const create: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.create.request)),
    mergeMap(({payload}) => forkJoin((payload.project_work_objects ?? []).map(object => apiRequests.workObject.add({
        data: {
            work_object_type_id: object.work_object_type.id,
            name: object.name,
        }
    }))).pipe(
        defaultIfEmpty([] as TypedAjaxResponse<RowResponse<ProjectWorkObject>>[]),
        mergeMap(responses => isSuccessArray(responses) ?
            of(responses.map(res => res.response.row)) :
            throwError(responses.filter(res => !isSuccess(res.response))[0])
        ),
        mergeMap(project_work_objects => apiRequests.project.add({
            data: {
                ...diffAndPrepare(EMPTY_PROJECT, payload),
                project_work_objects: unboxRecord({
                    add: unbox(project_work_objects.map(getId)),
                })
            },
        }))
    )),
    mergeMap(({response}) => response.success ?
        from([
            projectActions.create.success(response.row, 'user'),
            push(urlWithParams({projectId: response.row.id})(ROUTES.PROJECT_DETAIL)),
        ]) :
        of(projectActions.create.failure(toastFailedResponse(response, 'Не удалось создать проект')))
    ),
    catchError(err => of(projectActions.create.failure(toastFailedResponse(err, 'Не удалось создать проект')))),
)
const update: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(projectActions.update.request)),
    mergeMap((action) => {
        const changes = action.payload
        const projectId = changes.id
        const project_work_objects = pipeSelectors(projectId, projectObjectsSelector, objectsSelector)(state$.value)

        const project = projectSelector(projectId)(state$.value)
        if (!project) {
            return EMPTY
        }
        const {right: newObjects, left: oldObjects} = A.partition(isNew)(changes.project_work_objects ?? [])
        return forkJoin(newObjects.map(object => apiRequests.workObject.add({
            data: {name: object.name, work_object_type_id: object.work_object_type.id}
        }))).pipe(
            defaultIfEmpty([] as TypedAjaxResponse<RowResponse<ProjectWorkObject>>[]),
            mergeMap(responses => isSuccessArray(responses) ?
                of(responses.map(res => res.response.row)) :
                throwError(responses.filter(res => !isSuccess(res.response))[0])
            ),
            mergeMap(newObjects => apiRequests.project.update({
                params: {projectId: project.id},
                data: {
                    ...diffAndPrepare<ServerProject>({
                        ...project,
                        project_work_objects,
                    }, {
                        ...changes,
                        project_work_objects: unbox([...oldObjects, ...newObjects]),
                    }),
                    related: diffEntityArraySimple(project.related || [], changes.related || []),
                },
            }).pipe(
                mergeMap((res) => res.response.success ?
                    concat(
                        of(projectActions.update.success(res.response.row, 'user')),
                        action.meta.redirect ?
                            of(push(urlWithParams({projectId: res.response.row.id})(ROUTES.PROJECT_DETAIL))) :
                            EMPTY,
                    ) :
                    of(projectActions.update.failure(toastFailedResponse(res.response, 'Не удалось обновить проект')))
                ),
            ))
        )
    }),
    catchError(err => of(projectActions.update.failure(toastFailedResponse(err, 'Не удалось обновить проект')))),
)
const cancel: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(projectActions.cancel.request)),
    mergeMap(() => ProjectEditState.match({
        cancel: ({project}) => apiRequests.project.cancel({
            params: {
                projectId: project.id,
            }
        })
    }, () => EMPTY)(state$.value.data.project.editState)),
    mergeMap(res => res.response.success ?
        from([
            projectActions.cancel.success(res.response.row),
            projectActions.cancelEdit(),
        ]) :
        of(projectActions.cancel.failure(toastFailedResponse(res.response, 'Не удалось удалить проект')))
    ),
)

const close: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.close.request)),
    mergeMap(action => apiRequests.project.close({
        params: {projectId: action.payload.id},
        data: action.payload,
    })),
    map(res => res.response.success ?
        projectActions.close.success(res.response.row) :
        projectActions.close.failure(toastFailedResponse(res.response, 'Не удалось закрыть проект'))
    ),
)

const remove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(projectActions.remove.request)),
    mergeMap(() => ProjectEditState.match({
        delete: ({project}) => apiRequests.project.delete({params: {projectId: project.id}})
    }, () => EMPTY)(state$.value.data.project.editState)),
    mergeMap(({response}) => response.success ?
        from([
            projectActions.remove.success(response.row),
            projectActions.cancelEdit(),
            push(ROUTES.PROJECTS),
        ]) :
        of(projectActions.remove.failure(toastFailedResponse(response, 'Не удалось удалить проект'))),
    ),
)

const finplan: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.createFinplan.request)),
    mergeMap(({payload}) => apiRequests.project.createFinplan({params: {projectId: payload}})),
    map((res) => {
        if (res.response.success) {
            window.open(res.response.row.finplan?.link, '_blank')
            return projectActions.createFinplan.success(res.response.row)
        } else {
            return projectActions.createFinplan.failure(toastFailedResponse(res.response, 'Не удалось создать проект finplan'))
        }
    }),
)

const redirect: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.remove.success)),
    mergeMap(action => {
        const buildPath = urlWithParams({projectId: action.payload})
        const projectDetail = matchPath(location.pathname, {path: buildPath(ROUTES.PROJECT_DETAIL)})
        const projectEdit = matchPath(location.pathname, {path: buildPath(ROUTES.PROJECTS_EDIT)})
        if (projectDetail || projectEdit) {
            infoToast('Проект удален. Возврат на список проектов')
            return of(push(ROUTES.PROJECTS))
        }
        const taskPage = matchPath(location.pathname, {path: ROUTES.TASK})
        if (taskPage) {
            infoToast('Проект удален. Возврат на список задач')
            return of(push(ROUTES.TASKS))
        }
        return EMPTY
    })
)

const reservable: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(projectActions.reservable.init)),
    switchMapTo(action$.pipe(
        filter(isActionOf(projectActions.reservable.more)),
        concatMap(action => apiRequests.project.reservable({query: {
            list_size: action.payload,
            offset:state$.value.pages.archive.reservable.length
        }})),
        map(res => res.response.success?
            projectActions.reservable.success(res.response):
            projectActions.reservable.failure(toastFailedResponse(res.response, 'Ошибка загрузки проектов для переноса на стример')),
        ),
    ))
)

const checkSource: RootEpic = action$ => action$.pipe(
    filter(isActionOf(projectActions.checkSource.request)),
    mergeMap(action => apiRequests.project.checkSource({params: {projectId: action.payload}}).pipe(
        map(res => res.response.success?
            projectActions.checkSource.success(action.payload):
            projectActions.checkSource.failure(toastFailedResponse(res.response, 'Каталог не найден'))
        ),
    )),
)

export const projectEpic = combineEpics(
    get, list, archive, create, update, cancel, close, remove, finplan, redirect, reservable, checkSource
)